import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Activity, CheckCircle2 } from "lucide-react";

const ECGWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const ecgTab = tabs.find((tab) => tab.id === "ecg");
    const widget = ecgTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white via-white to-emerald-50 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-emerald-100 hover:shadow-emerald-200/50 transition-all duration-300">
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center">
                        <motion.div
                            className={`${isRTL ? "ml-3" : "mr-3"} p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-lg`}
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <Activity className="w-5 h-5 text-white" />
                        </motion.div>
                        <div>
                            <h5 className="text-emerald-800 font-semibold text-lg">{widget.title}</h5>
                            <p className="text-emerald-600 text-sm">{widget.subtitle}</p>
                        </div>
                    </div>
                    <motion.div className="text-right" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <div className="text-2xl font-bold text-emerald-700 mb-1">{widget.bpm}</div>
                        <div className="text-xs bg-gradient-to-r from-emerald-500 to-green-400 text-white px-3 py-1 rounded-full font-medium shadow-sm">{widget.status}</div>
                    </motion.div>
                </div>

                {/* ECG wave display */}
                <div className="relative h-32 mb-4 overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100/50">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/5 via-green-400/5 to-emerald-300/5"></div>

                    {/* ECG grid background */}
                    <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 128">
                        <defs>
                            <pattern id="ecgGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#10b981" strokeWidth="0.5" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#ecgGrid)" />
                    </svg>

                    {/* ECG wave */}
                    <div className="relative z-10 h-full flex items-center justify-center">
                        <motion.svg width="350" height="80" viewBox="0 0 350 80" className="overflow-visible">
                            <defs>
                                <linearGradient id="ecgGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.8" />
                                    <stop offset="50%" stopColor="#059669" stopOpacity="1" />
                                    <stop offset="100%" stopColor="#047857" stopOpacity="0.8" />
                                </linearGradient>
                            </defs>

                            <motion.path
                                d="M0,40 L30,40 L35,20 L40,60 L45,10 L50,40 L80,40 L85,20 L90,60 L95,10 L100,40 L130,40 L135,20 L140,60 L145,10 L150,40 L180,40 L185,20 L190,60 L195,10 L200,40 L230,40 L235,20 L240,60 L245,10 L250,40 L280,40 L285,20 L290,60 L295,10 L300,40 L330,40 L335,20 L340,60 L345,10 L350,40"
                                fill="none"
                                stroke="url(#ecgGradient)"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 1 }}
                                transition={{ duration: 2, ease: "easeInOut", repeat: Infinity }}
                            />

                            <motion.circle r="3" fill="#10b981" initial={{ x: 0, y: 40 }} animate={{ x: 350, y: 40 }} transition={{ duration: 2, ease: "linear", repeat: Infinity }} />
                        </motion.svg>
                    </div>

                    <div className="absolute top-2 left-2 text-xs text-emerald-600 bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg">{widget.rhythmStatus}</div>
                    <div className="absolute top-2 right-2 text-xs text-emerald-600 bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg">{widget.qrsNormal}</div>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <motion.div
                        className="text-center p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-emerald-100/50"
                        whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.8)" }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="text-lg font-bold text-emerald-700">72</div>
                        <div className="text-xs text-emerald-600">BPM</div>
                    </motion.div>
                    <motion.div
                        className="text-center p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-emerald-100/50"
                        whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.8)" }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="text-lg font-bold text-emerald-700">0.16</div>
                        <div className="text-xs text-emerald-600">QRS</div>
                    </motion.div>
                    <motion.div
                        className="text-center p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-emerald-100/50"
                        whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.8)" }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="text-lg font-bold text-emerald-700">0.42</div>
                        <div className="text-xs text-emerald-600">QT</div>
                    </motion.div>
                </div>

                {/* Overall status */}
                <motion.div
                    className="p-3 bg-gradient-to-r from-emerald-100/50 to-green-100/50 backdrop-blur-sm rounded-xl border border-emerald-200/30"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-emerald-500 rounded-full ml-2 animate-pulse"></div>
                            <span className="text-emerald-800 font-medium">{widget.heartStatus?.label}</span>
                        </div>
                        <span className="text-emerald-700 text-sm font-semibold">{widget.heartStatus?.value}</span>
                    </div>
                </motion.div>
            </div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-3 mt-3">
                {/* Rhythm status */}
                <motion.div
                    className="bg-gradient-to-br from-white to-emerald-50/50 backdrop-blur-sm rounded-xl p-3 border border-emerald-50 shadow-sm"
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-emerald-700 font-medium">{widget.rhythmWidget?.label}</span>
                        <motion.div className="text-green-500 w-5 h-5" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                            <CheckCircle2 className="w-5 h-5" />
                        </motion.div>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{widget.rhythmWidget?.sublabel}</span>
                        <motion.div className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-md flex items-center" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 ml-1"></span>
                            {widget.rhythmWidget?.value}
                        </motion.div>
                    </div>
                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-emerald-400 to-green-500 rounded-full"
                            initial={{ width: "0%" }}
                            animate={{ width: "85%" }}
                            transition={{ duration: 1, delay: 0.5 }}
                        />
                    </div>
                </motion.div>

                {/* Signal quality */}
                <motion.div
                    className="bg-gradient-to-br from-white to-emerald-50/50 backdrop-blur-sm rounded-xl p-3 border border-emerald-50 shadow-sm"
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-emerald-700 font-medium">{widget.signalWidget?.label}</span>
                        <motion.div className="text-emerald-500 w-5 h-5" animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                            <Activity className="w-5 h-5" />
                        </motion.div>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{widget.signalWidget?.sublabel}</span>
                        <motion.div className="text-xs bg-emerald-50 text-emerald-600 px-2 py-1 rounded-md flex items-center" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 ml-1"></span>
                            {widget.signalWidget?.value}
                        </motion.div>
                    </div>
                    <div className="mt-2 relative h-1.5">
                        <div className="absolute inset-0 flex items-center justify-between">
                            {[1, 2, 3, 4, 5].map((_, index) => (
                                <motion.div
                                    key={index}
                                    className="w-1 h-1 rounded-full bg-emerald-400"
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                />
                            ))}
                        </div>
                        <div className="absolute inset-0 flex items-center">
                            <motion.div className="h-0.5 bg-emerald-200 w-full" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8 }} />
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default ECGWidget;
